import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Feather, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { WordMark } from "@/components/brand/Logo";
import { PasswordInput, isPasswordValid } from "@/components/forms/PasswordInput";

const IMG_BG = "https://images.unsplash.com/photo-1508169351866-777fc0047ac5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxvcGVuJTIwYm9vayUyMHBhZ2VzJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3ODMyNzkzOTd8MA&ixlib=rb-4.1.0&q=85";

export default function AuthPage({ mode }) {
  const isRegister = mode === "register";
  const { login, register, user, error, setError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [roleChoice, setRoleChoice] = useState("author"); // author | beta_reader | both
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [busy, setBusy] = useState(false);

  // Parse `?next=<path>` so users bounce back to where they were after signing in.
  const params = new URLSearchParams(location.search);
  const rawNext = params.get("next") || "";
  const nextPath = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/dashboard";
  const expired = params.get("reason") === "expired";

  useEffect(() => {
    if (user && typeof user === "object") navigate(nextPath, { replace: true });
  }, [user, navigate, nextPath]);

  useEffect(() => {
    setError("");
  }, [mode, setError]);

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister) {
      if (!isPasswordValid(password)) {
        setError("Please meet every password requirement below.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords don't match.");
        return;
      }
    }
    setBusy(true);
    try {
      if (isRegister) {
        const roles = roleChoice === "both" ? ["author", "beta_reader"] : [roleChoice];
        await register({
          email,
          password,
          display_name: displayName || email.split("@")[0],
          roles,
          email_updates_opt_in: emailOptIn,
        });
      } else {
        await login(email, password);
      }
      navigate(nextPath);
    } catch {
      /* error surfaced through context */
    } finally {
      setBusy(false);
    }
  };

  // Legacy `demoLogin` (used the shared `Reader@123`/`Author@123` creds)
  // was removed — the public demo now goes through `startDemoSession` on
  // the "OR EXPLORE WITHOUT SIGNING UP" buttons below.


  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Illustration side */}
      <div className="hidden md:block relative overflow-hidden">
        <img src={IMG_BG} className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--sb-forest))]/85 via-[hsl(var(--sb-forest))]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/"><WordMark className="[&_span]:text-white [&_svg]:text-white" /></Link>
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.3em] text-white/70">Where great stories meet great readers.</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight">
              A marketplace built for the long arc of a writing career.
            </h2>
            <p className="mt-4 text-white/80 text-sm leading-relaxed">
              Trust. Relationships. Communication. Quality. Community.
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex flex-col p-6 sm:p-10">
        <div className="md:hidden mb-8"><Link to="/"><WordMark /></Link></div>
        <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center">
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
            {isRegister ? "Create your Last Bookmark account" : "Welcome back to Last Bookmark"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isRegister
              ? "Two minutes to sign up. Zero fees to explore."
              : "Sign in to continue where you left off."}
          </p>

          <div
            className="mt-5 rounded-2xl lantern-notice px-4 py-3"
            data-testid="auth-beta-notice"
          >
            <p className="text-sm font-bold text-[hsl(var(--sb-gold-2))] dark:text-[hsl(var(--sb-gold))]">
              Welcome to Last Bookmark — currently in beta
            </p>
            <p className="mt-1.5 text-sm leading-relaxed">
              Join a growing community of authors and beta readers helping shape the platform as we prepare for a wider launch soon. Some features are still being tested and improved, and feedback is always appreciated.
            </p>
          </div>

          {expired && !isRegister && (
            <div className="mt-4 rounded-xl lantern-notice px-4 py-3 text-sm font-medium"
              data-testid="auth-session-expired">
              Your session expired for security — sign in again to pick up where you left off.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {isRegister && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Display name</label>
                <input
                  type="text"
                  className="sb-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="How other writers see you"
                  data-testid={TID.auth.displayName}
                  required
                />
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email</label>
              <input
                type="email"
                className="sb-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@lastbookmark.app"
                data-testid={TID.auth.email}
                required
              />
            </div>
            <div>
              <PasswordInput
                value={password}
                onChange={setPassword}
                label="Password"
                placeholder={isRegister ? "At least 8 characters" : "Your password"}
                required
                autoComplete={isRegister ? "new-password" : "current-password"}
                showRules={isRegister}
                testId={TID.auth.password}
                confirmValue={isRegister ? confirmPassword : undefined}
                onConfirmChange={isRegister ? setConfirmPassword : undefined}
                confirmTestId="auth-confirm-password"
              />
            </div>

            {isRegister && (
              <div>
                <label className="text-sm font-medium mb-2 block">I want to join as</label>
                <div className="grid grid-cols-3 gap-2">
                  <RoleChip active={roleChoice === "author"} onClick={() => setRoleChoice("author")} icon={Feather} label="Author" testid={TID.auth.roleAuthor} />
                  <RoleChip active={roleChoice === "beta_reader"} onClick={() => setRoleChoice("beta_reader")} icon={BookOpen} label="Beta Reader" testid={TID.auth.roleBetaReader} />
                  <RoleChip active={roleChoice === "both"} onClick={() => setRoleChoice("both")} icon={Users} label="Both" testid={TID.auth.roleBoth} />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">You can add more roles later from your profile.</p>
              </div>
            )}

            {isRegister && (
              <label
                htmlFor="auth-email-opt-in"
                className="flex items-start gap-3 rounded-2xl border bg-muted/30 p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                data-testid="auth-email-opt-in-wrapper"
              >
                <input
                  id="auth-email-opt-in"
                  type="checkbox"
                  checked={emailOptIn}
                  onChange={(e) => setEmailOptIn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--sb-forest))] cursor-pointer"
                  data-testid="auth-email-opt-in"
                />
                <span className="text-xs leading-relaxed text-muted-foreground">
                  <span className="font-medium text-foreground">Send me occasional email updates</span> about new features, tips, and important announcements. You can unsubscribe any time from Settings → Email Preferences.
                </span>
              </label>
            )}

            {error && (
              <div data-testid={TID.auth.error} className="rounded-xl border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full py-3" disabled={busy} data-testid={TID.auth.submit}>
              {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            {isRegister ? "Already have an account? " : "New to Last Bookmark? "}
            <Link
              to={isRegister ? "/login" : "/register"}
              data-testid={TID.auth.switchMode}
              className="text-foreground link-underline font-medium"
            >
              {isRegister ? "Sign in" : "Create an account"}
            </Link>
          </p>
          {!isRegister && (
            <p className="mt-2 text-sm text-muted-foreground">
              <Link to="/forgot-password" className="link-underline" data-testid="auth-forgot-password">Forgot your password?</Link>
            </p>
          )}

          <div className="mt-8 pt-6 border-t space-y-3" data-testid="auth-demo-block">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold text-center">
              Or explore without signing up
            </p>
            <div className="grid grid-cols-2 gap-3">
              <DemoButton role="author" label="Demo Author" icon={Feather} />
              <DemoButton role="beta_reader" label="Demo Beta Reader" icon={BookOpen} />
            </div>
            <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
              Ephemeral demo session — nothing you change here is saved or seen by other users.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoButton({ role, label, icon: Icon }) {
  const { startDemoSession } = useAuth();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const go = async () => {
    setBusy(true);
    try {
      await startDemoSession(role);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      data-testid={`auth-demo-${role}`}
      className="flex flex-col items-center justify-center gap-1 rounded-2xl border-2 border-[hsl(var(--sb-forest))]/40 bg-card py-4 hover:bg-[hsl(var(--sb-moss))]/40 hover:border-[hsl(var(--sb-forest))] transition-colors disabled:opacity-60"
    >
      <Icon size={18} className="text-[hsl(var(--sb-forest))]" />
      <span className="text-sm font-medium">{busy ? "Loading…" : label}</span>
    </button>
  );
}

function RoleChip({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1 rounded-2xl border py-3 text-xs font-medium transition-all ${
        active
          ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))]"
          : "hover:bg-muted"
      }`}
    >
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );
}
